import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, ChevronLeft, ChevronRight, Phone, Plus, Check, ZoomIn, X, Share2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardFooter } from './ui/card';

const ProductCard = ({ product, whatsappNumber }) => {
  const navigate = useNavigate();
  const { addToCart, buyNow } = useCart();
  const [currentImage, setCurrentImage] = useState(0);
  const [isAdded, setIsAdded] = useState(false);
  const [showZoomModal, setShowZoomModal] = useState(false);
  
  const waNumber = whatsappNumber || '01111255596';
  const whatsappMessage = `Saya berminat dengan ${product.name} - RM${product.salePrice.toFixed(2)}`;
  const whatsappLink = `https://wa.me/6${waNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  // Use images array if available, otherwise fallback to image/image2
  const images = product.images || [product.image, product.image2].filter(Boolean);
  const hasMultipleImages = images.length > 1;

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleAddToCart = () => {
    addToCart(product);
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 1500);
  };

  const handleBuyNow = () => {
    buyNow(product);
    navigate('/checkout');
  };

  // Determine badge style
  const getBadgeConfig = () => {
    if (product.limitedEdition) return { label: 'Limited Edition', bg: 'bg-purple-600' };
    if (product.topSelling) return { label: 'Top Jualan', bg: 'bg-blue-600' };
    if (product.promoItem) return { label: 'Promo', bg: 'bg-green-600' };
    if (product.clearanceSale) return { label: 'Clearance', bg: 'bg-red-600' };
    if (product.bestSeller) return { label: 'Terlaris', bg: 'bg-amber-600' };
    return null;
  };

  const badgeConfig = getBadgeConfig();

  return (
    <>
      {/* Zoom Modal */}
      {showZoomModal && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowZoomModal(false)}
        >
          <button
            onClick={() => setShowZoomModal(false)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
            aria-label="Tutup"
          >
            <X size={24} />
          </button>
          
          {/* Full Image */}
          <img 
            src={images[currentImage]} 
            alt={product.name}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* Navigation in Modal */}
          {hasMultipleImages && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 text-white rounded-full flex items-center justify-center transition-colors"
                aria-label="Previous"
              >
                <ChevronLeft size={28} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 text-white rounded-full flex items-center justify-center transition-colors"
                aria-label="Next"
              >
                <ChevronRight size={28} />
              </button>
              
              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                {currentImage + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      )}

      <Card 
        className="group bg-white border border-gray-100 hover:border-amber-200 hover:shadow-lg transition-all duration-300 overflow-hidden"
        data-testid={`product-card-${product.id}`}
      >
      {/* Image Container */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        {/* Top Badge */}
        {badgeConfig && (
          <Badge className={`absolute top-2 left-2 z-10 ${badgeConfig.bg} text-white text-xs font-medium px-2 py-1`}>
            {badgeConfig.label}
          </Badge>
        )}
        
        {/* Discount Badge */}
        <Badge className="absolute top-2 right-2 z-10 bg-red-500 text-white text-xs font-bold px-2 py-1">
          -{product.discount}%
        </Badge>

        {/* Zoom Button */}
        <button
          onClick={() => setShowZoomModal(true)}
          className="absolute bottom-2 right-2 z-10 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
          aria-label="Zoom gambar"
          data-testid={`zoom-btn-${product.id}`}
        >
          <ZoomIn size={16} />
        </button>

        {/* Stock Badge - low stock warning or normal stock */}
        {product.stock > 0 && product.stock <= 2 && (
          <Badge className="absolute bottom-2 left-2 z-10 bg-red-600 text-white text-xs font-bold px-2 py-1 animate-pulse" data-testid={`low-stock-${product.id}`}>
            Tinggal {product.stock} unit!
          </Badge>
        )}
        {product.stock > 2 && (
          <Badge className="absolute bottom-2 left-2 z-10 bg-orange-500 text-white text-xs font-bold px-2 py-1">
            Stok: {product.stock} set
          </Badge>
        )}

        {/* Sold Out Overlay */}
        {product.soldOut && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
            <span className="bg-red-600 text-white font-bold text-lg px-4 py-2 rounded-lg transform -rotate-12">
              SOLD OUT
            </span>
          </div>
        )}
        
        {/* Main Image - Cropped in catalog */}
        <img 
          src={images[currentImage]} 
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 pointer-events-none select-none"
          loading="lazy"
          draggable="false"
          onContextMenu={(e) => e.preventDefault()}
        />
        
        {/* Navigation Arrows */}
        {hasMultipleImages && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 hover:bg-white text-gray-700 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
              aria-label="Previous image"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 hover:bg-white text-gray-700 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
              aria-label="Next image"
            >
              <ChevronRight size={18} />
            </button>
            
            {/* Image Indicators */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {images.slice(0, 5).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImage(index)}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    currentImage === index 
                      ? 'bg-white w-4' 
                      : 'bg-white/60 hover:bg-white/80'
                  }`}
                  aria-label={`Image ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Content */}
      <CardContent className="p-4">
        <h3 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2 min-h-[40px]">
          {product.name}
        </h3>
        
        {/* Features - Show max 3 */}
        {product.features && product.features.length > 0 && (
          <div className="mb-3 space-y-1">
            {product.features.slice(0, 3).map((feature, index) => (
              <div key={index} className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className="w-1 h-1 bg-amber-500 rounded-full"></span>
                <span className="truncate">{feature}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5 text-xs text-amber-600 font-medium">
              <span className="w-1 h-1 bg-amber-500 rounded-full"></span>
              <span>Lainnya...</span>
            </div>
          </div>
        )}
        
        {/* Price */}
        <div className="space-y-1">
          <div className="text-xl font-bold text-amber-700">
            RM{product.salePrice.toLocaleString()}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 line-through">
              RM{product.originalPrice.toLocaleString()}
            </span>
            <span className="text-xs font-medium text-green-600">
              Jimat RM{(product.originalPrice - product.salePrice).toLocaleString()}
            </span>
          </div>
        </div>
      </CardContent>

      {/* Footer */}
      <CardFooter className="p-4 pt-0 flex flex-col gap-2">
        {product.soldOut ? (
          <Button 
            disabled
            className="w-full bg-gray-400 text-white text-sm font-medium h-10 cursor-not-allowed"
          >
            SOLD OUT
          </Button>
        ) : (
          <Button 
            onClick={handleBuyNow}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium h-10"
            data-testid={`buy-now-btn-${product.id}`}
          >
            <ShoppingCart size={16} className="mr-1.5" />
            Beli Sekarang
          </Button>
        )}
        <button
          onClick={() => {
            const siteUrl = window.location.origin;
            const text = `🪑 *${product.name}*\n💰 RM${product.salePrice.toLocaleString()} (Jimat RM${(product.originalPrice - product.salePrice).toLocaleString()}!)\n\n👉 Lihat di sini: ${siteUrl}`;
            window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
          }}
          className="w-full flex items-center justify-center gap-1.5 text-green-600 hover:bg-green-50 text-xs font-medium py-2 rounded-lg border border-green-200 hover:border-green-300 transition-colors"
          data-testid={`share-wa-btn-${product.id}`}
        >
          <Phone size={14} />
          Kongsi ke WhatsApp
        </button>
      </CardFooter>
    </Card>
    </>
  );
};

export default ProductCard;